// @flow

//
// React
//
import React, { useState, useEffect } from 'react';

//
// React Router
//
import { useNavigate, useLocation } from "react-router-dom";

//
// Redux Fantasy
//
import {
    useLocalSelector,
} from '../../../../../lib/redux-fantasy-reducers';

//
// Formatter
//
import {
    fmtDate,
    fmtTime,
    fmtMoney,
    fmtDocumentNumber
} from '../../../../../lib/trix-web-data-commons';

//
// Material UI
//
import { makeStyles } from '@mui/styles';

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import {
    Card,
    CardContent,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import { Select, MenuItem } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import { motion } from "framer-motion";

//
// Extender
//
import withHalFormsAction from '../../../../../lib/trix-web-hal-forms/extender/withHalFormsAction';

//
// Redux Fantasy
//
import {
    getActionFactory,
	getSelector,
} from '../../../../../lib/trix-web-hal-forms/tools/halFormsRedux';

//
// Styles
//
const useStyles = makeStyles(theme => ({
    root: {
    },
}));

//
// Props
//
type Props = {
    //
    // Hal forms template
    //
    __halFormsTemplate: any,

    //
    // Ducks
    //
    __ducks: {
        selectors: any,
        actions: any,
    },

    //
    // Container
    //
    container: any,

    //
    // Data
    //
    data: any,

    //
    // Callbacks
    //
    onClose: () => void,
} // End of Props

//
// Component
//
const RetailSalePaymentForm = (props: Props): any => {
    const inProgress = useLocalSelector (
        getSelector 
                (props.__ducks) 
                (props.__halFormsTemplate.actions[0].id) 
                ('isInProgress'));

    const paymentDue = (props.data && props.data.paymentDue) ? props.data.paymentDue : {};

    const [paymentMethod, setPaymentMethod] = useState(null);
    const [enteredAmount, setEnteredAmount] = useState(0.0);
    const [currency, setCurrency] = useState((paymentDue.totalAmount) ? paymentDue.totalAmount.currency : '');

    useEffect(() => {
        if (paymentDue && paymentDue.totalAmount && currency !== paymentDue.totalAmount.currency) {
            setCurrency(paymentDue.totalAmount.currency);
        }
    }, [ paymentDue ]);

    let change = { amount: 0.0, currency };
    if (paymentMethod === 'cash') {
        const leftDueAmount = paymentDue.totalAmount.amount - paymentDue.paidAmount.amount;
        if (enteredAmount > leftDueAmount) {
            change = {
                amount: enteredAmount - leftDueAmount,
                currency,
            }
        }
    }

    if (!paymentDue || !paymentDue.totalAmount) {
        return null;
    }

    return (
        <Box p={2}>
            <Stack spacing={3}>

                <Stack>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2" color="text.secondary">Сума продажба:</Typography>
                        <Typography variant="subtitle1" >{fmtMoney(paymentDue.totalAmount)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2" color="text.secondary">Платени:</Typography>
                        <Typography variant="subtitle1" >{fmtMoney(paymentDue.paidAmount)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2" color="text.secondary">Обща сума за плащане:</Typography>
                        <Typography variant="subtitle1" fontWeight={600}>{fmtMoney({
                            amount: paymentDue.totalAmount.amount - paymentDue.paidAmount.amount,
                            currency: paymentDue.totalAmount.currency
                        })}</Typography>
                    </Box>
                </Stack>

                {/* Payment method selection */}
                <ToggleButtonGroup
                    exclusive
                    value={paymentMethod}
                    onChange={(_, value) => value && setPaymentMethod(value)}
                    fullWidth
                >
                    <ToggleButton value="card">
                        <CreditCardIcon sx={{ mr: 1 }} /> Карта
                    </ToggleButton>
                    <ToggleButton value="cash">
                        <PaymentsIcon sx={{ mr: 1 }} /> В брой
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Payment details */}
                {paymentMethod && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Stack spacing={2}>

                            <TextField
                                label={
                                    paymentMethod === "card"
                                        ? "Сума за плащане"
                                        : "Получена сума в брой"
                                }
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={enteredAmount}
                                onChange={(e) => setEnteredAmount(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <Select
                                            value={currency}
                                            onChange={(e) => setCurrency(e.target.value)}
                                            variant="standard"
                                            disableUnderline
                                            sx={{
                                                minWidth: 64,
                                                ml: 1,
                                                '& .MuiSelect-select': {
                                                    paddingRight: '24px'
                                                }
                                            }}
                                        >
                                            <MenuItem value="EUR">EUR</MenuItem>
                                            <MenuItem value="BGN">BGN</MenuItem>
                                        </Select>
                                    )
                                }}
                            />

                            {(paymentMethod === "cash" && enteredAmount && change.amount > 0) ? (
                                <Typography variant="body2">
                                    Ресто: <strong>{fmtMoney(change)}</strong>
                                </Typography>
                            ) : null}
                        </Stack>
                    </motion.div>
                )}

                {/* Optional action button */}
                {paymentMethod && (
                    <Button
                        variant="contained"
                        disabled={!paymentMethod || inProgress}
                        onClick={(event) => {
                            let action = null;
                            for (let n in props.__halFormsTemplate.actions) {
                                if (props.__halFormsTemplate.actions[n].name === 'onPay') {
                                    action = props.__halFormsTemplate.actions[n];
                                    break;
                                }
                            }

                            if (action) {
                                let amountToPay = {
                                    amount: enteredAmount,
                                    currency
                                }
                                const leftDueAmount = paymentDue.totalAmount.amount - paymentDue.paidAmount.amount;
                                if (enteredAmount > leftDueAmount) {
                                    amountToPay = {
                                        amount: leftDueAmount,
                                        currency,
                                    }
                                }

                                props.__halFormsActionExtender.executeHalFormsAction(
                                    action, 

                                    // compose action data
                                    {
                                        paymentMethod,
                                        amountToPay
                                    },
                                    
                                    // container is current data
                                    props.data, 

                                    // meta
                                    {}
                                );
                            }

                            return;
                        }}
                    >
                        Потвърди плащане
                    </Button>
                )}
            </Stack>
        </Box>
    );
};
export default withHalFormsAction ({}) (RetailSalePaymentForm);